import {
  BioAnalyteTypeText,
  BioComplianceStatusValueType,
  BioSample,
  BioSampleAliquotIn,
  BioSampleCode,
  BioSampleCodeFormatType,
  BioSampleCollectIn,
  BioSampleCommentIn,
  BioSampleContainer,
  BioSampleContainerNumber,
  BioSampleDeliveryLab,
  BioSampleDeliveryLabReasonType,
  BioSampleDisplayContainerNumber,
  BioSampleId,
  BioSampleIdCustomStatusIns,
  BioSampleKitCode,
  BioSampleMetaType,
  BioSampleProcessedValueType,
  BioSampleProcessIn,
  BioSampleRefType,
  BioSampleShipIn,
  BioSampleStatusType,
  BioSampleUpdateBaseIn,
  BioSampleUpdateError,
  BioSampleValueType,
  CorrelationIdAndErrorV1,
  ShippingNumber,
} from '@csp/csp-common-hbs-model';
import { ApiOptions, DmdpError, IsoDateStr, Maybe, Patient, TimeStr, VisitId } from '@csp/csp-common-model';
import { ScheduleVersionRequestRef } from '@csp/csp-common-scheduling';
import { isDefined } from '@csp/csp-common-util';
import { toRestOptions } from '@csp/csp-fe-auth';
import { BioSamplingRestServiceV1 } from '@csp/dmdp-api-client';
import {
  BioSampleAndIdInV1,
  BioSampleBatchCreateResultsV1,
  BioSampleBatchCreateResultV1,
  BioSampleBatchUpsertCustomStatusesResultsV1,
  type BioSampleBatchUpsertCustomStatusesResultV1,
  BioSampleIdAndCustomStatusesInV1,
  BioSampleIdsAndCustomStatusesInV1,
  BioSamplesAndIdsInV1,
} from '@csp/dmdp-api-hbs-dto';
import { chunk } from 'lodash';
import { BioSampleErrorMapper } from '../mapper/BioSampleErrorMapper';
import { BioSampleInMapper } from '../mapper/BioSampleInMapper';
import { BioSampleMapper } from '../mapper/BioSampleMapper';
import { BioSampleMetaMapper } from '../mapper/BioSampleMetaMapper';
import { BioSampleContainerUtil } from '../util/BioSampleContainerUtil';

const SAMPLES_POST_CHUNK_SIZE = 50;

const doBatchOperation = async ({
  samplesIn,
  request,
}: {
  samplesIn: BioSample[];
  request: (sample: BioSample) => Promise<BioSample>;
}): Promise<BioSampleUpdateError<BioSample>[]> => {
  const bioSampleIdOrErrorsV1: CorrelationIdAndErrorV1[] = [];

  for (const sample of samplesIn) {
    try {
      const updatedBioSample = await request(sample);

      bioSampleIdOrErrorsV1.push({
        correlationId: updatedBioSample.sampleId,
      });
    } catch (error) {
      const dmdpError = error as DmdpError;
      bioSampleIdOrErrorsV1.push({
        correlationId: sample.sampleId,
        error: {
          errorId: dmdpError.errorId || '',
          errorCode: dmdpError.errorCode || '',
          message: dmdpError.message,
        },
      });
    }
  }

  return BioSampleErrorMapper.fromResult(
    bioSampleIdOrErrorsV1,
    samplesIn.map(sample => ({
      updateIn: sample,
      correlationId: sample.sampleId,
    })),
  );
};

const setManyCustomStatusesInChunks = async (
  samples: BioSampleIdAndCustomStatusesInV1[],
  apiOptions?: ApiOptions,
): Promise<BioSampleBatchUpsertCustomStatusesResultsV1> => {
  const chunks = chunk(samples, SAMPLES_POST_CHUNK_SIZE);
  const results = await Promise.allSettled(
    chunks.map(chunk =>
      BioSamplingRestServiceV1.setManyCustomStatuses(toRestOptions(apiOptions), {
        bioSampleIdsAndCustomStatuses: chunk,
      }),
    ),
  );

  const fulfilledResults = results
    .filter(result => result.status === 'fulfilled')
    .map(result => (result as PromiseFulfilledResult<BioSampleBatchUpsertCustomStatusesResultsV1>).value)
    .flatMap(chunk => chunk.bioSampleBatchUpsertCustomStatusesResults);

  const rejectedResults: BioSampleBatchUpsertCustomStatusesResultV1[] = samples
    .filter(sampleIn => !fulfilledResults.some(sampleInV1 => sampleInV1.bioSampleId === sampleIn.bioSampleId))
    .map(result => ({ bioSampleId: result.bioSampleId, error: { errorId: 'e9999', errorCode: 'UNEXPECTED ERROR' } }));

  const allResults: BioSampleBatchUpsertCustomStatusesResultV1[] = [...fulfilledResults, ...rejectedResults];

  //ToDo - Yoshi - log errors to sentry

  return { bioSampleBatchUpsertCustomStatusesResults: allResults };
};

const setManyCustomStatuses = async <T extends BioSampleUpdateBaseIn>(
  sampleIns: T[],
  insV1: BioSampleIdCustomStatusIns[],
  apiOptions?: ApiOptions,
): Promise<BioSampleUpdateError<T>[]> => {
  const inWithCorrIdV1s: BioSampleIdsAndCustomStatusesInV1 = {
    bioSampleIdsAndCustomStatuses: insV1.map(customStatusV1 => ({
      bioSampleId: customStatusV1.sampleId,
      customStatuses: customStatusV1.customStatusInV1s.customStatuses ?? [],
    })),
  };

  const bioSampleBatchUpsertCustomStatusesResultsV1 = await setManyCustomStatusesInChunks(
    inWithCorrIdV1s.bioSampleIdsAndCustomStatuses,
    apiOptions,
  );

  return BioSampleErrorMapper.fromBioSampleBatchUpsertCustomStatusesResultsV1(
    bioSampleBatchUpsertCustomStatusesResultsV1,
    sampleIns.map(sampleIn => ({
      updateIn: sampleIn,
      correlationId: sampleIn.sampleId,
    })),
  );
};

const saveManyBioSamplesInChunks = async (
  samples: BioSampleAndIdInV1[],
  apiOptions?: ApiOptions,
): Promise<BioSampleBatchCreateResultsV1> => {
  const chunks = chunk(samples, SAMPLES_POST_CHUNK_SIZE);
  const results = await Promise.allSettled(
    chunks.map(chunk =>
      BioSamplingRestServiceV1.createManyBioSamples(toRestOptions(apiOptions), { bioSamples: chunk }),
    ),
  );

  const fulfilledResults = results
    .filter(result => result.status === 'fulfilled')
    .map(result => (result as PromiseFulfilledResult<BioSampleBatchCreateResultsV1>).value)
    .flatMap(chunk => chunk.bioSampleBatchCreateResults);

  const rejectedResults: BioSampleBatchCreateResultV1[] = samples
    .filter(sampleIn => !fulfilledResults.some(sampleInV1 => sampleInV1.correlationId === sampleIn.correlationId))
    .map(result => ({
      correlationId: result.correlationId,
      error: { errorId: 'e9998', errorCode: 'UNEXPECTED ERROR' },
    }));

  const allResults: BioSampleBatchCreateResultV1[] = [...fulfilledResults, ...rejectedResults];

  //ToDo - Yoshi - log errors to sentry

  return { bioSampleBatchCreateResults: allResults };
};

const saveManyCollectedBioSamples = async (
  sampleIns: BioSampleCollectIn[],
  apiOptions?: ApiOptions,
): Promise<BioSampleUpdateError<BioSampleCollectIn>[]> => {
  const inV1s: BioSamplesAndIdsInV1 = {
    bioSamples: sampleIns.map(sampleIn => ({
      bioSample: BioSampleInMapper.fromCollectInToBioSampleInV1(sampleIn),
      correlationId: sampleIn.sampleCode,
    })),
  };
  const bioSampleIdOrErrorsV1 = await saveManyBioSamplesInChunks(inV1s.bioSamples, apiOptions);

  return BioSampleErrorMapper.fromBioSampleBatchCreateResultsV1(
    bioSampleIdOrErrorsV1,
    sampleIns.map(sampleIn => ({
      updateIn: sampleIn,
      correlationId: sampleIn.sampleCode,
    })),
  );
};

const saveManyAliquotedBioSamples = async (
  sampleIns: BioSampleAliquotIn[],
  apiOptions?: ApiOptions,
): Promise<BioSampleUpdateError<BioSampleAliquotIn>[]> => {
  const inV1s: BioSamplesAndIdsInV1 = {
    bioSamples: sampleIns.map(sampleIn => ({
      bioSample: BioSampleInMapper.fromAliquotInToBioSampleInV1(sampleIn),
      correlationId: sampleIn.sampleCode,
    })),
  };

  const bioSampleIdOrErrorsV1 = await saveManyBioSamplesInChunks(inV1s.bioSamples, apiOptions);

  return BioSampleErrorMapper.fromBioSampleBatchCreateResultsV1(
    bioSampleIdOrErrorsV1,
    sampleIns.map(sampleIn => ({
      updateIn: sampleIn,
      correlationId: sampleIn.sampleCode,
    })),
  );
};

const saveProcessedBioSamples = async (
  sampleIns: BioSampleProcessIn[],
  apiOptions?: ApiOptions,
): Promise<BioSampleUpdateError<BioSampleProcessIn>[]> =>
  setManyCustomStatuses<BioSampleProcessIn>(
    sampleIns,
    sampleIns.map(sampleIn => BioSampleInMapper.fromProcessInToBioSampleInV1(sampleIn)),
    apiOptions,
  );

const saveShippedBioSamples = async (
  sampleIns: BioSampleShipIn[],
  apiOptions?: ApiOptions,
): Promise<BioSampleUpdateError<BioSampleShipIn>[]> =>
  setManyCustomStatuses<BioSampleShipIn>(
    sampleIns,
    sampleIns.map(sampleIn => BioSampleInMapper.fromShipInToBioSampleInV1(sampleIn)),
    apiOptions,
  );

const saveUsedBioSample = async (
  bioSample: BioSample,
  commentIn: BioSampleCommentIn,
  apiOptions?: ApiOptions,
): Promise<BioSample> => {
  const bioSampleV1 = await BioSamplingRestServiceV1.setCustomStatus(
    toRestOptions(apiOptions),
    bioSample.sampleId,
    BioSampleStatusType.SHIPPING,
    BioSampleInMapper.toUsedBioSampleCustomStatusInV1(commentIn),
  );
  return BioSampleMapper.toBioSample(bioSampleV1);
};

const updateBioSamplesCollectionDateTime = async ({
  sampleIns,
  dateIn,
  timeIn,
  apiOptions,
}: {
  sampleIns: BioSample[];
  dateIn: IsoDateStr;
  timeIn: TimeStr;
  apiOptions?: ApiOptions;
}): Promise<BioSampleUpdateError<BioSample>[]> =>
  setManyCustomStatuses<BioSample>(
    sampleIns,
    sampleIns.map(({ sampleId, collectionStatus }) => ({
      sampleId,
      customStatusInV1s: {
        customStatuses: [
          BioSampleInMapper.fromDateTimeToCollectionStatusInV1({
            collectionStatus,
            dateIn,
            timeIn,
          }),
        ],
      },
    })),
    apiOptions,
  );

const updateBioSamplesProcessingStartAndEndDateTime = async ({
  sampleIns,
  startDateIn,
  startTimeIn,
  endDateIn,
  endTimeIn,
  apiOptions,
}: {
  sampleIns: BioSample[];
  startDateIn: IsoDateStr;
  startTimeIn: TimeStr;
  endDateIn: IsoDateStr;
  endTimeIn: TimeStr;
  apiOptions?: ApiOptions;
}): Promise<BioSampleUpdateError<BioSample>[]> =>
  setManyCustomStatuses<BioSample>(
    sampleIns,
    sampleIns.map(({ sampleId, processingStatus }) => ({
      sampleId,
      customStatusInV1s: {
        customStatuses: [
          BioSampleInMapper.fromDateTimeToInProcessStatusInV1({
            currentStatus: processingStatus.evaluatedStatusValue,
            dateIn: startDateIn,
            timeIn: startTimeIn,
          }),
          ...(endDateIn && endTimeIn
            ? [
                BioSampleInMapper.fromDateTimeToProcessedStatusInV1({
                  currentStatus: processingStatus.evaluatedStatusValue,
                  dateIn: endDateIn,
                  timeIn: endTimeIn,
                }),
              ]
            : []),
        ],
      },
    })),
    apiOptions,
  );

const updateBioSamplesProcessCompliance = async ({
  sampleIns,
  complianceIn,
  apiOptions,
}: {
  sampleIns: BioSample[];
  complianceIn: BioComplianceStatusValueType;
  apiOptions?: ApiOptions;
}): Promise<BioSampleUpdateError<BioSample>[]> =>
  setManyCustomStatuses<BioSample>(
    sampleIns,
    sampleIns.map(({ sampleId, processComplianceStatus }) => ({
      sampleId,
      customStatusInV1s: {
        customStatuses: [
          BioSampleInMapper.fromValueToProcessComplianceStatusInV1({
            currentStatus: processComplianceStatus,
            value: complianceIn,
          }),
        ],
      },
    })),
    apiOptions,
  );

const updateBioSamplesStorageCompliance = async ({
  sampleIns,
  complianceIn,
  apiOptions,
}: {
  sampleIns: BioSample[];
  complianceIn: BioComplianceStatusValueType;
  apiOptions?: ApiOptions;
}): Promise<BioSampleUpdateError<BioSample>[]> =>
  setManyCustomStatuses<BioSample>(
    sampleIns,
    sampleIns.map(({ sampleId, storageComplianceStatus }) => ({
      sampleId,
      customStatusInV1s: {
        customStatuses: [
          BioSampleInMapper.fromValueToStorageComplianceStatusInV1({
            currentStatus: storageComplianceStatus,
            value: complianceIn,
          }),
        ],
      },
    })),
    apiOptions,
  );

const addComment = async (
  bioSample: BioSample,
  commentIn: BioSampleCommentIn,
  apiOptions?: ApiOptions,
): Promise<BioSample> => {
  if (commentIn.comment?.trim()) {
    const bioSampleV1 = await BioSamplingRestServiceV1.setCustomStatus(
      toRestOptions(apiOptions),
      bioSample.sampleId,
      BioSampleStatusType.COMMENT,
      BioSampleInMapper.fromCommentInToBioSampleInV1(commentIn),
    );
    return BioSampleMapper.toBioSample(bioSampleV1);
  } else {
    return bioSample;
  }
};

const fetchById = async (sampleId: BioSampleId, apiOptions?: ApiOptions): Promise<BioSample> =>
  BioSampleMapper.toBioSample(await BioSamplingRestServiceV1.getById(toRestOptions(apiOptions), sampleId));

const updateBioSamplePatient = async (
  { sampleId, patient }: { sampleId: BioSampleId; patient: Patient },
  apiOptions?: ApiOptions,
): Promise<BioSample> => {
  const bioSampleV1 = await BioSamplingRestServiceV1.setBioSampleUserId(
    toRestOptions(apiOptions),
    sampleId,
    patient.userId,
  );

  return BioSampleMapper.toBioSample(bioSampleV1);
};

const updateBioSampleVisit = async (
  { sampleId, requestRef }: { sampleId: BioSampleId; requestRef: ScheduleVersionRequestRef },
  apiOptions?: ApiOptions,
): Promise<BioSample> => {
  const bioSampleV1 = await BioSamplingRestServiceV1.setBioSampleScheduleRequestRef(
    toRestOptions(apiOptions),
    sampleId,
    requestRef,
  );

  return BioSampleMapper.toBioSample(bioSampleV1);
};

const updateBioSampleVisitIdRef = async (
  { sampleId, visitId }: { sampleId: BioSampleId; visitId: VisitId },
  apiOptions?: ApiOptions,
): Promise<BioSample> => {
  const bioSampleV1 = await BioSamplingRestServiceV1.setBioSampleRef(toRestOptions(apiOptions), sampleId, {
    key: BioSampleRefType.VISIT_ID,
    value: visitId,
  });

  return BioSampleMapper.toBioSample(bioSampleV1);
};

const updateBioSamplePatientAndVisit = async (
  {
    sampleId,
    patient,
    requestRef,
    visitId,
  }: { sampleId: BioSampleId; patient: Patient; requestRef: ScheduleVersionRequestRef; visitId: VisitId },
  apiOptions?: ApiOptions,
): Promise<BioSample> => {
  await updateBioSamplePatient({ sampleId, patient }, apiOptions);
  await updateBioSampleVisit({ sampleId, requestRef }, apiOptions);
  const bioSampleWithUpdatedVisitIdRef = await updateBioSampleVisitIdRef({ sampleId, visitId }, apiOptions);

  return bioSampleWithUpdatedVisitIdRef;
};

const updateBioSamplesPatientAndVisit = async (
  {
    samples,
    patient,
    requestRef,
    visitId,
  }: { samples: BioSample[]; patient: Patient; requestRef: ScheduleVersionRequestRef; visitId: VisitId },
  apiOptions?: ApiOptions,
): Promise<BioSampleUpdateError<BioSample>[]> =>
  doBatchOperation({
    samplesIn: samples,
    request: sample =>
      updateBioSamplePatientAndVisit({ sampleId: sample.sampleId, patient, requestRef, visitId }, apiOptions),
  });

const updateBioSampleCode = async (
  { sample, bioSampleCode }: { sample: BioSample; bioSampleCode: BioSampleCode },
  apiOptions?: ApiOptions,
): Promise<BioSample> => {
  const bioSampleV1 = await BioSamplingRestServiceV1.setBioSampleCode(toRestOptions(apiOptions), sample.sampleId, {
    code: bioSampleCode.trim(),
    format: sample.format,
  });

  return BioSampleMapper.toBioSample(bioSampleV1);
};

const syncBioSampleContainerInfo = async (
  { bioSample, bioSampleContainer }: { bioSample: BioSample; bioSampleContainer: Maybe<BioSampleContainer> },
  apiOptions?: ApiOptions,
): Promise<BioSample> => {
  const { kitChanges, containerChanges } = BioSampleContainerUtil.evalContainerInfoFieldsDiff(
    bioSample,
    bioSampleContainer,
  );

  let bioSampleV1 = undefined;
  if (kitChanges) {
    bioSampleV1 = await BioSamplingRestServiceV1.setBioSampleKitInfo(toRestOptions(apiOptions), bioSample.sampleId, {
      kitType: bioSampleContainer?.kitType,
      kitDescription: bioSampleContainer?.kitDescription,
      kitExpirationDate: bioSampleContainer?.kitExpirationDateStr,
    });
  }

  if (containerChanges) {
    bioSampleV1 = await BioSamplingRestServiceV1.setBioSampleContainerInfo(
      toRestOptions(apiOptions),
      bioSample.sampleId,
      {
        containerType: bioSampleContainer?.containerType,
        containerDescription: bioSampleContainer?.containerDescription,
      },
    );
  }

  return bioSampleV1 ? BioSampleMapper.toBioSample(bioSampleV1) : bioSample;
};

const updateBioSampleKitCode = async (
  { sampleId, bioSampleKitCode }: { sampleId: BioSampleId; bioSampleKitCode: BioSampleKitCode },
  apiOptions?: ApiOptions,
): Promise<BioSample> => {
  const bioSampleV1 = await BioSamplingRestServiceV1.setBioSampleKitCode(toRestOptions(apiOptions), sampleId, {
    sampleKitCode: bioSampleKitCode.trim(),
  });

  return BioSampleMapper.toBioSample(bioSampleV1);
};

const updateBioSampleContainerNumber = async (
  {
    sampleId,
    format,
    containerNumber,
  }: {
    sampleId: BioSampleId;
    format: BioSampleCodeFormatType;
    containerNumber: BioSampleContainerNumber | BioSampleDisplayContainerNumber;
  },
  apiOptions?: ApiOptions,
): Promise<BioSample> => {
  const bioSampleV1 = await BioSamplingRestServiceV1.setBioSampleContainerNumber(
    toRestOptions(apiOptions),
    sampleId,
    BioSampleInMapper.toContainerNumberInV1(format, containerNumber),
  );

  return BioSampleMapper.toBioSample(bioSampleV1);
};

const updateBioSamplesAnalyteType = async ({
  sampleIns,
  analyteType,
  apiOptions,
}: {
  sampleIns: BioSample[];
  analyteType: BioAnalyteTypeText;
  apiOptions?: ApiOptions;
}): Promise<BioSampleUpdateError<BioSample>[]> =>
  setManyCustomStatuses<BioSample>(
    sampleIns,
    sampleIns.map(({ sampleId }) => ({
      sampleId,
      customStatusInV1s: {
        customStatuses: [{ type: BioSampleStatusType.ANALYTE_TYPE, value: analyteType }],
      },
    })),
    apiOptions,
  );

const updateBioSamplesSampleType = async ({
  sampleIns,
  sampleType,
  apiOptions,
}: {
  sampleIns: BioSample[];
  sampleType: BioSampleValueType;
  apiOptions?: ApiOptions;
}): Promise<BioSampleUpdateError<BioSample>[]> =>
  setManyCustomStatuses<BioSample>(
    sampleIns,
    sampleIns.map(({ sampleId }) => ({
      sampleId,
      customStatusInV1s: {
        customStatuses: [{ type: BioSampleStatusType.SAMPLE_TYPE, value: sampleType }],
      },
    })),
    apiOptions,
  );

const updateBioSamplesProcessSampleType = async ({
  sampleIns,
  processSampleType,
  apiOptions,
}: {
  sampleIns: BioSample[];
  processSampleType: BioSampleProcessedValueType;
  apiOptions?: ApiOptions;
}): Promise<BioSampleUpdateError<BioSample>[]> =>
  setManyCustomStatuses<BioSample>(
    sampleIns,
    sampleIns.map(({ sampleId }) => ({
      sampleId,
      customStatusInV1s: {
        customStatuses: [{ type: BioSampleStatusType.PROCESSED_SAMPLE_TYPE, value: processSampleType }],
      },
    })),
    apiOptions,
  );

const updateBioSamplesCode = async (
  {
    samples,
    bioSampleCode,
  }: {
    samples: BioSample[];
    bioSampleCode: BioSampleCode;
  },
  apiOptions?: ApiOptions,
): Promise<BioSampleUpdateError<BioSample>[]> =>
  doBatchOperation({
    samplesIn: samples,
    request: sample => updateBioSampleCode({ sample, bioSampleCode }, apiOptions),
  });

const updateBioSamplesContainerNumber = async (
  {
    samples,
    bioSampleContainerNumber,
  }: {
    samples: BioSample[];
    bioSampleContainerNumber: BioSampleContainerNumber | BioSampleDisplayContainerNumber;
  },
  apiOptions?: ApiOptions,
): Promise<BioSampleUpdateError<BioSample>[]> =>
  doBatchOperation({
    samplesIn: samples,
    request: sample =>
      updateBioSampleContainerNumber(
        {
          sampleId: sample.sampleId,
          format: sample.format,
          containerNumber: bioSampleContainerNumber,
        },
        apiOptions,
      ),
  });

const updateBioSamplesKitCode = async (
  {
    samples,
    bioSampleKitCode,
  }: {
    samples: BioSample[];
    bioSampleKitCode: BioSampleKitCode;
  },
  apiOptions?: ApiOptions,
): Promise<BioSampleUpdateError<BioSample>[]> =>
  doBatchOperation({
    samplesIn: samples,
    request: sample => updateBioSampleKitCode({ sampleId: sample.sampleId, bioSampleKitCode }, apiOptions),
  });

const setAsAliquotParent = async (
  bioSample: BioSample,
  {
    markAsUsed,
    usedComment,
  }: {
    markAsUsed: boolean;
    usedComment: Maybe<string>;
  } = { markAsUsed: false, usedComment: undefined },
  apiOptions?: ApiOptions,
): Promise<BioSample> => {
  if (markAsUsed) {
    await BioSampleService.saveUsedBioSample(bioSample, { comment: usedComment }, apiOptions);
  }
  const v1 = await BioSamplingRestServiceV1.setMeta(
    toRestOptions(apiOptions),
    bioSample.sampleId,
    BioSampleMetaType.ALIQUOT_V1,
    BioSampleMetaMapper.toMetaInV1(BioSampleMetaMapper.toParentAliquotMetaV1()),
  );
  return BioSampleMapper.toBioSample(v1);
};

const updateBioSamplesShippingDateNumberAndDeliveryLab = async (
  {
    sampleIns,
    shippingDateIn,
    shippingNumberIn,
    deliveryLabIn,
    deliveryLabReasonIn,
  }: {
    sampleIns: BioSample[];
    shippingDateIn?: IsoDateStr;
    shippingNumberIn?: ShippingNumber;
    deliveryLabIn?: BioSampleDeliveryLab;
    deliveryLabReasonIn?: BioSampleDeliveryLabReasonType;
  },
  apiOptions?: ApiOptions,
): Promise<BioSampleUpdateError<BioSample>[]> => {
  if (shippingDateIn || shippingNumberIn || deliveryLabIn) {
    return setManyCustomStatuses<BioSample>(
      sampleIns,
      sampleIns.map(({ sampleId, shippingStatus, shippingNumber, deliveryLab }) => ({
        sampleId,
        customStatusInV1s: {
          customStatuses: [
            shippingDateIn
              ? BioSampleInMapper.fromDateToShippingStatusInV1({
                  currentStatus: shippingStatus,
                  date: shippingDateIn,
                })
              : undefined,
            shippingNumberIn
              ? BioSampleInMapper.fromValueToShippingNumberStatusInV1({
                  currentStatus: shippingNumber,
                  value: shippingNumberIn,
                })
              : undefined,
            deliveryLabIn && deliveryLabReasonIn
              ? BioSampleInMapper.fromValueAndReasonToDeliveryLabStatusInV1({
                  currentStatus: deliveryLab,
                  value: deliveryLabIn,
                  reasonText: deliveryLabReasonIn,
                })
              : undefined,
          ].filter(isDefined),
        },
      })),
      apiOptions,
    );
  } else {
    return [];
  }
};

export const BioSampleService = {
  saveManyCollectedBioSamples,
  saveManyAliquotedBioSamples,
  saveProcessedBioSamples,
  saveShippedBioSamples,
  saveUsedBioSample,
  updateBioSamplesCollectionDateTime,
  updateBioSamplesPatientAndVisit,
  updateBioSamplesCode,
  updateBioSamplesContainerNumber,
  updateBioSamplesKitCode,
  updateBioSamplesAnalyteType,
  updateBioSamplesSampleType,
  updateBioSamplesProcessSampleType,
  syncBioSampleContainerInfo,
  addComment,
  fetchById,
  setAsAliquotParent,
  updateBioSamplesStorageCompliance,
  updateBioSamplesProcessCompliance,
  updateBioSamplesShippingDateNumberAndDeliveryLab,
  updateBioSamplesProcessingStartAndEndDateTime,
};
